import { Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, signal, computed, inject, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LayoutService } from '../../../../core/services/layout.service';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { StoryCategory, FamilySpace, FamilyMember } from '../../../../core/models/story.models';

export interface Question {
  id: string;
  text: string;
  selected: boolean;
  source?: string;
  sourceType?: number;
  isCustom?: boolean;
}

@Component({
  selector: 'app-create-schedule-promt',
  imports: [CommonModule, FormsModule],
  templateUrl: './create-schedule-promt.html',
  styleUrl: './create-schedule-promt.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:click)': 'closeAllMenus()' },
})
export class CreateSchedulePromt implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);
  private readonly api = inject(ApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);

  // -- Step navigation -------------------------------------------------------

  readonly currentStep = signal<number>(1);
  readonly totalSteps = 4;

  // -- Validation submitted flags -------------------------------------------
  readonly step4Submitted = signal(false);

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) this.currentStep.set(step);
  }

  nextStep(): void {
    const next = this.currentStep() + 1;
    this.goToStep(next);
    if (next === 3) {
      const catId = this.selectedCategory();
      if (catId) this.loadPrompts(catId);
    }
  }

  prevStep(): void {
    this.goToStep(this.currentStep() - 1);
  }

  cancelSchedule(): void {
    this.router.navigate(['/app/scheduleprompt']);
  }

  // -- Step 1: Categories ----------------------------------------------------

  readonly loadingCategories = signal(false);
  readonly apiCategories = signal<StoryCategory[]>([]);
  readonly selectedCategory = signal<number | null>(null);

  selectCategory(id: number): void {
    this.selectedCategory.set(id);
  }

  getCategoryIcon(cat: StoryCategory): string {
    return cat.iconUrl?.trim() || 'images/icons/memory-share-icons/fm-ms-icon1.svg';
  }

  readonly selectedCategoryName = computed(() => {
    const cat = this.apiCategories().find(c => c.categoryId === this.selectedCategory());
    return cat?.categoryName ?? '';
  });

  async loadCategories(): Promise<void> {
    this.loadingCategories.set(true);
    try {
      const res = await this.api.request<any>(
        'GET', API_ROUTES.SP_GET_CATEGORIES, undefined,
        { showLoader: false, showToaster: false },
      );
      if (res.status && res.data) {
        const list: StoryCategory[] = Array.isArray(res.data)
          ? res.data
          : res.data.categories ?? [];
        this.apiCategories.set(list);
        if (list.length) this.selectedCategory.set(list[0].categoryId);
      }
    } finally {
      this.loadingCategories.set(false);
      this.cdr.detectChanges();
    }
  }

  // -- Step 2: Media type ----------------------------------------------------

  readonly mediaTypes = [
    { id: 'video',       label: 'Video Recording', description: 'They can record or upload video responses',              icon: 'images/icons/memory-share-icons/fm-ts-icon1.svg' },
    { id: 'audio',       label: 'Audio Recording', description: 'They can record or upload audio responses',              icon: 'images/icons/memory-share-icons/fm-ts-icon2.svg' },
    { id: 'image-audio', label: 'Image + Audio',   description: 'You provide a image, they answer with video or audio',   icon: 'images/icons/memory-share-icons/fm-ts-icon3.svg' },
  ];

  readonly selectedMedia = signal<string>('video');

  readonly selectedMediaLabel = computed(() => {
    const m = this.mediaTypes.find(t => t.id === this.selectedMedia());
    return m?.label ?? '';
  });

  selectMedia(id: string): void {
    this.selectedMedia.set(id);
  }

  // -- Step 3: Questions -----------------------------------------------------

  readonly loadingPrompts = signal(false);
  readonly questions = signal<Question[]>([]);
  readonly selectedQuestions = computed(() => this.questions().filter(q => q.selected));

  readonly openMenuId = signal<string | null>(null);
  readonly showCustomQuestion = signal(false);
  readonly customQuestionText = signal('');
  readonly editingQuestionId = signal<string | null>(null);
  readonly editingQuestionText = signal('');
  readonly deleteConfirmId = signal<string | null>(null);
  readonly savingCustomQuestion = signal(false);
  readonly savingEditQuestion = signal(false);

  async loadPrompts(categoryId: number): Promise<void> {
    this.loadingPrompts.set(true);
    this.questions.set([]);
    try {
      const res = await this.api.request<{ questionId: string; questionText: string; source: string; sourceType: number }[]>(
        'GET', `${API_ROUTES.SP_GET_QUESTIONS_BY_CATEGORY}/${categoryId}`, undefined,
        { showLoader: false, showToaster: false },
      );
      const items = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
      if (res.status && items.length) {
        this.questions.set(
          items.map(p => ({
            id: p.questionId,
            text: p.questionText,
            source: p.source,
            sourceType: p.sourceType,
            selected: false,
            isCustom: p.sourceType === 2,
          })),
        );
      }
    } finally {
      this.loadingPrompts.set(false);
      this.cdr.detectChanges();
    }
  }

  toggleQuestion(id: string): void {
    this.questions.update(list => list.map(q => q.id === id ? { ...q, selected: !q.selected } : q));
  }

  selectAllQuestions(): void {
    this.questions.update(list => list.map(q => ({ ...q, selected: true })));
  }

  clearAllQuestions(): void {
    this.questions.update(list => list.map(q => ({ ...q, selected: false })));
  }

  toggleQuestionMenu(id: string, event: Event): void {
    event.stopPropagation();
    this.openMenuId.update(current => current === id ? null : id);
  }

  closeAllMenus(): void {
    this.openMenuId.set(null);
  }

  toggleCustomQuestion(): void {
    this.showCustomQuestion.update(v => !v);
  }

  onQuestionCardSpace(event: Event, id: string): void {
    if (this.editingQuestionId() !== id) {
      event.preventDefault();
      this.toggleQuestion(id);
    }
  }

  async saveCustomQuestion(): Promise<void> {
    const text = this.customQuestionText().trim();
    if (!text || text.length > 500) return;
    if (this.savingCustomQuestion()) return;
    const categoryId = this.selectedCategory();
    if (!categoryId) return;

    this.savingCustomQuestion.set(true);
    try {
      const res = await this.api.request<string>(
        'POST', API_ROUTES.ADD_CUSTOM_QUESTION,
        { categoryId, questionText: text },
        { showLoader: false, showToaster: false },
      );
      if (res.status && res.data) {
        this.questions.update(list => [
          ...list,
          { id: res.data!, text, source: 'Custom', sourceType: 2, selected: true, isCustom: true },
        ]);
      }
      this.customQuestionText.set('');
      this.showCustomQuestion.set(false);
    } finally {
      this.savingCustomQuestion.set(false);
      this.cdr.detectChanges();
    }
  }

  startEditQuestion(id: string): void {
    const q = this.questions().find(q => q.id === id);
    if (!q?.isCustom) return;
    this.editingQuestionId.set(id);
    this.editingQuestionText.set(q.text);
    this.openMenuId.set(null);
  }

  cancelEditQuestion(): void {
    this.editingQuestionId.set(null);
    this.editingQuestionText.set('');
  }

  async saveEditQuestion(): Promise<void> {
    const id = this.editingQuestionId();
    const text = this.editingQuestionText().trim();
    if (!id || !text || text.length > 500) return;
    if (this.savingEditQuestion()) return;

    this.savingEditQuestion.set(true);
    try {
      const res = await this.api.request<unknown>(
        'POST', API_ROUTES.UPDATE_CUSTOM_QUESTION,
        { customQuestionId: id, questionText: text },
        { showLoader: false, showToaster: false },
      );
      if (res.status) {
        this.questions.update(list => list.map(q => q.id === id ? { ...q, text } : q));
      }
      this.editingQuestionId.set(null);
      this.editingQuestionText.set('');
    } finally {
      this.savingEditQuestion.set(false);
      this.cdr.detectChanges();
    }
  }

  deleteQuestion(id: string): void {
    this.openMenuId.set(null);
    this.deleteConfirmId.set(id);
  }

  cancelDelete(): void {
    this.deleteConfirmId.set(null);
  }

  confirmDelete(): void {
    const id = this.deleteConfirmId();
    if (!id) return;
    this.deleteConfirmId.set(null);
    const q = this.questions().find(q => q.id === id);
    if (q?.isCustom) {
      this.deleteCustomQuestionApi(id);
    } else {
      this.questions.update(list => list.filter(q => q.id !== id));
    }
  }

  private async deleteCustomQuestionApi(id: string): Promise<void> {
    const res = await this.api.request<unknown>(
      'POST', API_ROUTES.DELETE_CUSTOM_QUESTION,
      { requestId: id },
      { showLoader: false, showToaster: false },
    );
    if (res.status) {
      this.questions.update(list => list.filter(q => q.id !== id));
    }
    this.cdr.detectChanges();
  }

  // -- Step 4: Send to -------------------------------------------------------

  readonly familySpaces = signal<FamilySpace[]>([]);
  readonly familyMembers = signal<FamilyMember[]>([]);
  readonly selectedFamilyFilter = signal<string>('all');
  readonly selectedMemberIds = signal<Set<string>>(new Set());
  readonly frequencyTypes = signal<{ id: number; name: string }[]>([]);
  readonly promptFrequency = signal<number>(1);
  readonly questionsPerPrompt = signal('1');
  readonly promptTitle = signal('');
  readonly publishing = signal(false);

  readonly filteredMembers = computed(() => {
    const filter = this.selectedFamilyFilter();
    const members = this.familyMembers();
    if (filter === 'all') return members;
    return members.filter(m => m.familySpaceId === filter);
  });

  selectFamilyFilter(spaceId: string): void {
    this.selectedFamilyFilter.set(spaceId);
  }

  toggleMember(memberId: string): void {
    this.selectedMemberIds.update(set => {
      const next = new Set(set);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }

  isMemberSelected(memberId: string): boolean {
    return this.selectedMemberIds().has(memberId);
  }

  getMemberInitials(member: FamilyMember): string {
    const parts = (member.fullName || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : '';
    return (first + last).toUpperCase() || 'NA';
  }

  readonly selectedFrequencyName = computed(() => {
    const f = this.frequencyTypes().find(t => t.id === this.promptFrequency());
    return f?.name ?? '';
  });

  readonly cycleDuration = computed(() => {
    const totalQuestions = this.selectedQuestions().length;
    const perPrompt = parseInt(this.questionsPerPrompt(), 10) || 1;
    const cycles = Math.ceil(totalQuestions / perPrompt);
    const freqName = this.selectedFrequencyName().toLowerCase();
    if (!cycles || !freqName) return '—';
    return `${cycles} ${freqName}${cycles !== 1 ? 's' : ''} — ${perPrompt} question${perPrompt !== 1 ? 's' : ''} per ${freqName}`;
  });

  readonly selectedMemberNames = computed(() => {
    const ids = this.selectedMemberIds();
    const members = this.familyMembers();
    return members.filter(m => ids.has(m.familyMemberId)).map(m => m.fullName);
  });

  async loadFamilyData(): Promise<void> {
    const [spacesRes, membersRes] = await Promise.all([
      this.api.request<FamilySpace[]>('GET', API_ROUTES.GET_ACTIVE_FAMILY_LIST, undefined, { showLoader: false, showToaster: false }),
      this.api.request<FamilyMember[]>('GET', API_ROUTES.GET_FAMILY_MEMBER_LIST, undefined, { showLoader: false, showToaster: false }),
    ]);
    if (spacesRes.status && spacesRes.data) {
      const list = Array.isArray(spacesRes.data) ? spacesRes.data : [];
      this.familySpaces.set(list);
    }
    if (membersRes.status && membersRes.data) {
      const list = Array.isArray(membersRes.data) ? membersRes.data : [];
      console.log('[SchedulePromt] familyMembers loaded:', list.length, list.map(m => ({ name: m.fullName, image: m.image })));
      this.familyMembers.set(list);
    }
    this.cdr.detectChanges();
  }

  async loadFrequencyTypes(): Promise<void> {
    const res = await this.api.request<any>(
      'POST', API_ROUTES.COMMON_GET_ALL_MASTER_LIST,
      { typeIds: [5] },
      { showLoader: false, showToaster: false },
    );
    if (res.status && res.data) {
      const list = res.data.frequencyTypeList ?? res.data.FrequencyTypeList ?? [];
      this.frequencyTypes.set(list);
      if (list.length) this.promptFrequency.set(list[0].id);
    }
    this.cdr.detectChanges();
  }

  async sendPrompt(): Promise<void> {
    this.step4Submitted.set(true);
    if (!this.promptTitle().trim()) return;
    if (this.promptTitle().length > 250) return;
    if (this.selectedMemberIds().size === 0) return;
    if (this.publishing()) return;
    const categoryId = this.selectedCategory();
    if (!categoryId) return;

    const mediaMap: Record<string, number> = { video: 1, audio: 2, 'image-audio': 3 };
    const mediaType = mediaMap[this.selectedMedia()] ?? 1;

    const questions = this.selectedQuestions().map((q, idx) => ({
      questionId: q.id,
      questionSource: q.sourceType ?? (q.isCustom ? 2 : 1),
      questionOrder: idx + 1,
    }));

    const invitedMemberIds = Array.from(this.selectedMemberIds());

    this.publishing.set(true);
    try {
      const res = await this.api.request<any>(
        'POST', API_ROUTES.SP_CREATE_AND_PUBLISH,
        {
          categoryId,
          mediaType,
          questions,
          frequencyType: this.promptFrequency(),
            questionPerPrompt: parseInt(this.questionsPerPrompt(), 10),
          invitedMemberIds,
          promptTitle: this.promptTitle().trim(),
        },
        { showLoader: true, showToaster: true },
      );
      if (res.status) {
        this.router.navigate(['/app/scheduleprompt']);
      }
    } finally {
      this.publishing.set(false);
      this.cdr.detectChanges();
    }
  }

  // -- Reload warning --------------------------------------------------------

  readonly showReloadWarning = signal(false);

  private readonly onBeforeUnload = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const isRefresh = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key === 'r');
    if (isRefresh) { e.preventDefault(); this.showReloadWarning.set(true); }
  };

  dismissReloadWarning(): void { this.showReloadWarning.set(false); }

  confirmReload(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    location.reload();
  }

  // -- Lifecycle -------------------------------------------------------------

  ngOnInit(): void {
    this.layoutService.setHideHeader(true);
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('beforeunload', this.onBeforeUnload);
      window.addEventListener('keydown', this.onKeyDown);
      this.loadCategories();
      this.loadFamilyData();
      this.loadFrequencyTypes();
    }
  }

  ngOnDestroy(): void {
    this.layoutService.setHideHeader(false);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
  }
}
